import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
  ReferenceArea,
} from 'recharts';
import { useParams, useNavigate } from 'react-router-dom';
import { api, Quote, Prediction, EarningsQuoteReport, EarningsPrediction } from '../api';
import { useAuth } from '../AuthContext';

const DAYS_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 5, label: '5 Days' },
  { value: 10, label: '10 Days' },
  { value: 30, label: '1 Month' },
  { value: 60, label: '2 Months' },
  { value: 365, label: '1 Year' },
  { value: 1825, label: '5 Years' },
  { value: 3650, label: '10 Years' },
];
const DAYS_VALUES = DAYS_OPTIONS.map((o) => o.value);

function parseDaysParam(view?: string): number {
  if (!view) return 10;
  const match = view.match(/^(\d+)/);
  return match && DAYS_VALUES.includes(Number(match[1])) ? Number(match[1]) : 10;
}

export default function StockCharts() {
  const { symbol: urlSymbol, view: urlView } = useParams<{ symbol?: string; view?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbol, setSymbol] = useState(urlSymbol?.toUpperCase() || '');
  const [days, setDays] = useState(parseDaysParam(urlView));
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const s = user?.stocks || [];
    setSymbols(s);
    if (!symbol && s.length > 0) setSymbol(s[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.stocks]);

  // Update URL when symbol or days change
  useEffect(() => {
    if (symbol) {
      navigate(`/charts/stock/${symbol}/view/${days}days`, { replace: true });
    }
  }, [symbol, days, navigate]);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    Promise.all([
      api.getQuotes(symbol, days),
      api.getPredictions(symbol, 'daily'),
    ])
      .then(([q, p]) => {
        setQuotes(q);
        setPredictions(p);
      })
      .finally(() => setLoading(false));
  }, [symbol, days]);

  // Build a prediction lookup by date
  const predByDay = React.useMemo(() => {
    const map: Record<string, Prediction> = {};
    for (const p of predictions) {
      const day = p.date?.slice(0, 10);
      if (day) map[day] = p;
    }
    return map;
  }, [predictions]);

  const today = new Date().toISOString().slice(0, 10);

  // Find the most recent date in quotes data
  const latestDay = React.useMemo(() => {
    if (quotes.length === 0) return today;
    const days = quotes.map((q) => q.date?.slice(0, 10)).filter(Boolean) as string[];
    return days.sort().pop() || today;
  }, [quotes, today]);

  // For 1-day view: show hourly points for the latest day
  const intradayData = React.useMemo(() => {
    const dayQuotes = quotes.filter((q) => q.date?.slice(0, 10) === latestDay);
    // Group by hour, keep last entry per hour
    const byHour: Record<string, Quote> = {};
    for (const q of dayQuotes) {
      const hour = new Date(q.timestamp * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      if (!byHour[hour] || q.timestamp > byHour[hour].timestamp) {
        byHour[hour] = q;
      }
    }
    const dayPrediction = predByDay[latestDay];
    return Object.keys(byHour)
      .sort()
      .map((hour) => ({
        date: hour,
        price: byHour[hour]?.currentPrice ?? null,
        high: byHour[hour]?.highPriceDay ?? null,
        low: byHour[hour]?.lowPriceDay ?? null,
        change: byHour[hour]?.percentChange ?? null,
        predicted: dayPrediction?.predictedQuote ?? null,
        recommendation: dayPrediction?.recommendation ?? null,
      }));
  }, [quotes, predByDay, latestDay]);

  // For multi-day view: group by day, keep last entry per day
  const dailyData = React.useMemo(() => {
    const byDay: Record<string, Quote> = {};
    for (const q of quotes) {
      const day = q.date?.slice(0, 10);
      if (!day) continue;
      if (!byDay[day] || q.timestamp > byDay[day].timestamp) {
        byDay[day] = q;
      }
    }
    // Only include predictions from today onwards
    const futurePredDays = Object.keys(predByDay).filter((d) => d >= today);
    const allDays = new Set([...Object.keys(byDay), ...futurePredDays]);
    return Array.from(allDays)
      .sort()
      .map((day) => ({
        date: day,
        price: byDay[day]?.currentPrice ?? null,
        high: byDay[day]?.highPriceDay ?? null,
        low: byDay[day]?.lowPriceDay ?? null,
        change: byDay[day]?.percentChange ?? null,
        predicted: predByDay[day]?.predictedQuote ?? null,
        recommendation: predByDay[day]?.recommendation ?? null,
      }));
  }, [quotes, predByDay, today]);

  const chartData = days === 1 ? intradayData : dailyData;

  const latestQuote = quotes.length > 0 ? quotes[quotes.length - 1] : null;
  const previousClose = latestQuote?.previousClosePrice;

  return (
    <div>
      <h2>Stock Charts</h2>

      <div className="controls">
        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
          {symbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {DAYS_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>

      {/* Latest price card */}
      {latestQuote && (
        <div className="card-grid" style={{ marginBottom: 24 }}>
          <div className="card">
            <h3>{symbol}</h3>
            <div className="stats-row">
              <div className="stat">
                <div className="value">${latestQuote.currentPrice?.toFixed(2)}</div>
                <div className="label">Current Price</div>
              </div>
              <div className="stat">
                <div className="value" style={{ color: (latestQuote.change ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                  {(latestQuote.change ?? 0) >= 0 ? '+' : ''}{latestQuote.change?.toFixed(2)} ({latestQuote.percentChange?.toFixed(2)}%)
                </div>
                <div className="label">Change</div>
              </div>
            </div>
            <div className="meta">
              Day Range: ${latestQuote.lowPriceDay?.toFixed(2)} - ${latestQuote.highPriceDay?.toFixed(2)}
              &nbsp;&middot;&nbsp; Date: {latestQuote.date}
            </div>
          </div>
        </div>
      )}

      {/* Price chart */}
      <div className="chart-container">
        <h3 style={{ marginBottom: 16 }}>{days === 1 ? `Intraday — ${latestDay}` : 'Price History'}</h3>
        {loading ? (
          <div className="loading">Loading chart...</div>
        ) : chartData.length === 0 ? (
          <div className="loading">No quote data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="date" stroke="#8b8fa3" fontSize={12} interval={days >= 365 ? Math.floor(chartData.length / 12) : 'preserveStartEnd'} angle={days >= 365 ? -45 : 0} textAnchor={days >= 365 ? 'end' : 'middle'} height={days >= 365 ? 60 : 30} />
              <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{
                  background: '#1a1d27',
                  border: '1px solid #2a2d3a',
                  borderRadius: 8,
                  color: '#e1e4eb',
                }}
              />
              {previousClose && (
                <ReferenceLine y={previousClose} stroke="#8b8fa3" strokeDasharray="3 3" label="Prev Close" />
              )}
              <Line type="monotone" dataKey="price" stroke="#6366f1" strokeWidth={2} dot={days < 365 ? { r: 3 } : false} name="Price" connectNulls />
              <Line type="monotone" dataKey="predicted" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={days < 365 ? { r: 4, fill: '#f59e0b' } : false} name="Predicted" connectNulls />
              <Line type="monotone" dataKey="high" stroke="#22c55e" strokeWidth={1} strokeDasharray="4 4" dot={false} name="High" connectNulls />
              <Line type="monotone" dataKey="low" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Low" connectNulls />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Intraday / all quotes table */}
      {quotes.length > 0 && (
        <div className="card">
          <h3>All Quotes ({quotes.length})</h3>
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            <table className="summary-table">
              <thead>
                <tr><th>Date</th><th>Price</th><th>Change</th><th>High</th><th>Low</th></tr>
              </thead>
              <tbody>
                {(() => {
                  let lastDate = '';
                  return [...quotes].reverse().map((q, i) => {
                    const day = q.date?.slice(0, 10) || '';
                    const showHeader = day !== lastDate;
                    lastDate = day;
                    return (
                      <React.Fragment key={i}>
                        {showHeader && (
                          <tr>
                            <td colSpan={5} style={{
                              background: 'rgba(99, 102, 241, 0.08)',
                              fontWeight: 700,
                              fontSize: 13,
                              padding: '10px 12px',
                              color: 'var(--accent)',
                              borderTop: '2px solid var(--border)',
                            }}>
                              {day}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>{q.date}</td>
                          <td>${q.currentPrice?.toFixed(2)}</td>
                          <td style={{ color: (q.change ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                            {(q.change ?? 0) >= 0 ? '+' : ''}{q.change?.toFixed(2)} ({q.percentChange?.toFixed(2)}%)
                          </td>
                          <td>${q.highPriceDay?.toFixed(2)}</td>
                          <td>${q.lowPriceDay?.toFixed(2)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Earnings Impact Chart */}
      <EarningsImpactChart symbols={symbols} initialSymbol={symbol} />
    </div>
  );
}

/* ─── Earnings Impact Chart ─────────────────────────────────────────── */

function EarningsImpactChart({ symbols, initialSymbol }: { symbols: string[]; initialSymbol: string }) {
  const [earningsSymbol, setEarningsSymbol] = useState(initialSymbol);
  const [reports, setReports] = useState<EarningsQuoteReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState<EarningsPrediction | null>(null);
  const [predicting, setPredicting] = useState(false);
  const [predictionError, setPredictionError] = useState<string>('');
  const [showPrediction, setShowPrediction] = useState<'pattern' | 'adjusted' | 'both'>('both');

  // Sync with parent symbol selection
  useEffect(() => {
    setEarningsSymbol(initialSymbol);
  }, [initialSymbol]);

  // Fetch available earnings reports when symbol changes
  useEffect(() => {
    if (!earningsSymbol) return;
    setLoading(true);
    api.getEarningsQuotes(earningsSymbol)
      .then((data) => {
        setReports(data);
        if (data.length > 0) {
          setSelectedReport(`${data[0].year}-Q${data[0].quarter}-${data[0].freq || 'quarterly'}`);
        } else {
          setSelectedReport('');
        }
      })
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [earningsSymbol]);

  const activeReport = React.useMemo(() => {
    if (!selectedReport || reports.length === 0) return null;
    return reports.find(
      (r) => `${r.year}-Q${r.quarter}-${r.freq || 'quarterly'}` === selectedReport
    ) || null;
  }, [selectedReport, reports]);

  const chartData = React.useMemo(() => {
    if (!activeReport) return [];
    return activeReport.quotes.map((q) => ({
      date: q.date,
      close: q.close,
      high: q.high,
      low: q.low,
      isReportDay: q.isReportDay,
      change: q.percentChange,
    }));
  }, [activeReport]);

  // Load saved prediction from localStorage when symbol changes
  useEffect(() => {
    setPredictionError('');
    try {
      const saved = localStorage.getItem(`earnings-prediction-${earningsSymbol}`);
      if (saved) {
        setPrediction(JSON.parse(saved));
      } else {
        setPrediction(null);
      }
    } catch {
      setPrediction(null);
    }
  }, [earningsSymbol]);

  const handlePredict = async () => {
    setPredicting(true);
    setPredictionError('');
    setPrediction(null);
    try {
      const result = await api.predictEarningsImpact(earningsSymbol);
      setPrediction(result);
      try { localStorage.setItem(`earnings-prediction-${earningsSymbol}`, JSON.stringify(result)); } catch {}
    } catch (err: any) {
      setPredictionError(err.message || 'Prediction failed');
    } finally {
      setPredicting(false);
    }
  };

  // Build prediction chart data
  const predictionChartData = React.useMemo(() => {
    if (!prediction) return [];
    const days = ['day1', 'day2', 'day3', 'day4', 'day5'] as const;
    return days.map((d, i) => ({
      date: `Day +${i + 1}`,
      patternClose: prediction.patternPrediction[d].predictedClose,
      adjustedClose: prediction.newsAdjustedPrediction[d].predictedClose,
      patternPct: prediction.patternPrediction[d].pctChange,
      adjustedPct: prediction.newsAdjustedPrediction[d].pctChange,
    }));
  }, [prediction]);

  // Combined data: recent prices + predicted days
  const combinedPredictionData = React.useMemo(() => {
    if (!prediction) return [];
    const recent = (prediction.recentPrices || []).map((p) => ({
      date: p.date,
      close: p.close,
      patternClose: null as number | null,
      adjustedClose: null as number | null,
    }));
    // Add bridge point: last real price is also first prediction anchor
    const lastPrice = recent.length > 0 ? recent[recent.length - 1].close : prediction.lastClose;
    const days = ['day1', 'day2', 'day3', 'day4', 'day5'] as const;
    const preds = days.map((d, i) => ({
      date: `Day +${i + 1}`,
      close: null as number | null,
      patternClose: prediction.patternPrediction[d].predictedClose,
      adjustedClose: prediction.newsAdjustedPrediction[d].predictedClose,
    }));
    // Bridge: duplicate last real as start of prediction lines
    if (recent.length > 0) {
      recent[recent.length - 1] = {
        ...recent[recent.length - 1],
        patternClose: lastPrice,
        adjustedClose: lastPrice,
      };
    }
    return [...recent, ...preds];
  }, [prediction]);

  // Find report day index for highlighting
  const reportDayDate = activeReport?.filedDate || '';

  return (
    <div className="chart-container" style={{ marginTop: 32 }}>
      <h3 style={{ marginBottom: 16 }}>Earnings Impact</h3>
      <div className="controls" style={{ marginBottom: 16 }}>
        <select value={earningsSymbol} onChange={(e) => setEarningsSymbol(e.target.value)}>
          {symbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={selectedReport}
          onChange={(e) => setSelectedReport(e.target.value)}
          disabled={reports.length === 0}
        >
          {reports.length === 0 && <option value="">No reports available</option>}
          {reports.map((r) => (
            <option key={`${r.year}-Q${r.quarter}-${r.freq || 'quarterly'}`} value={`${r.year}-Q${r.quarter}-${r.freq || 'quarterly'}`}>
              {r.label || `Q${r.quarter} ${r.year}`} — Filed {r.filedDate}
            </option>
          ))}
        </select>
        <button
          onClick={handlePredict}
          disabled={predicting}
          style={{
            padding: '6px 16px',
            background: predicting ? '#4b5563' : '#8b5cf6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: predicting ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {predicting ? '🔮 Predicting...' : '🔮 Predict'}
        </button>
        {prediction && !predicting && (
          <>
            <span style={{ fontSize: 11, color: '#8b8fa3' }}>
              Saved {new Date(prediction.generatedAt).toLocaleDateString()}
            </span>
            <button
              onClick={() => {
                setPrediction(null);
                try { localStorage.removeItem(`earnings-prediction-${earningsSymbol}`); } catch {}
              }}
              style={{
                padding: '4px 10px',
                background: 'transparent',
                color: '#8b8fa3',
                border: '1px solid #2a2d3a',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
              }}
            >
              Clear
            </button>
          </>
        )}
      </div>

      {predictionError && (
        <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>
          Prediction error: {predictionError}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading earnings data...</div>
      ) : chartData.length === 0 ? (
        <div className="loading">No earnings quote data available</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
              <XAxis dataKey="date" stroke="#8b8fa3" fontSize={12} />
              <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{
                  background: '#1a1d27',
                  border: '1px solid #2a2d3a',
                  borderRadius: 8,
                  color: '#e1e4eb',
                }}
                formatter={(value: any, name: any) => {
                  if (name === 'Close') return [`$${Number(value).toFixed(2)}`, name];
                  return [value, name];
                }}
                labelFormatter={(label: any) =>
                  String(label) === reportDayDate ? `📊 ${label} (Report Filed)` : String(label)
                }
              />
              {/* Highlight the report filing day */}
              {reportDayDate && (
                <ReferenceLine
                  x={reportDayDate}
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  label={{ value: 'Report Filed', position: 'top', fill: '#f59e0b', fontSize: 12 }}
                />
              )}
              {/* Shade the "before" region */}
              {chartData.length > 0 && reportDayDate && chartData[0].date !== reportDayDate && (
                <ReferenceArea
                  x1={chartData[0].date}
                  x2={reportDayDate}
                  fill="#6366f1"
                  fillOpacity={0.06}
                  label={{ value: '5 days before', position: 'insideTopLeft', fill: '#8b8fa3', fontSize: 11 }}
                />
              )}
              {/* Shade the "after" region */}
              {chartData.length > 0 && reportDayDate && chartData[chartData.length - 1].date !== reportDayDate && (
                <ReferenceArea
                  x1={reportDayDate}
                  x2={chartData[chartData.length - 1].date}
                  fill="#22c55e"
                  fillOpacity={0.06}
                  label={{ value: '5 days after', position: 'insideTopRight', fill: '#8b8fa3', fontSize: 11 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="close"
                stroke="#6366f1"
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  if (payload.isReportDay) {
                    return (
                      <svg key={`dot-${payload.date}`}>
                        <circle cx={cx} cy={cy} r={7} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                      </svg>
                    );
                  }
                  return <circle key={`dot-${payload.date}`} cx={cx} cy={cy} r={3} fill="#6366f1" stroke="none" />;
                }}
                name="Close"
                connectNulls
              />
              <Line type="monotone" dataKey="high" stroke="#22c55e" strokeWidth={1} strokeDasharray="4 4" dot={false} name="High" connectNulls />
              <Line type="monotone" dataKey="low" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Low" connectNulls />
              <Legend />
            </LineChart>
          </ResponsiveContainer>

          {/* Report details card */}
          {activeReport && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="stats-row">
                <div className="stat">
                  <div className="value">{activeReport.form || '10-Q'}</div>
                  <div className="label">Form</div>
                </div>
                <div className="stat">
                  <div className="value">Q{activeReport.quarter} {activeReport.year}</div>
                  <div className="label">Quarter</div>
                </div>
                <div className="stat">
                  <div className="value">{activeReport.filedDate}</div>
                  <div className="label">Filed Date</div>
                </div>
                {chartData.length >= 2 && (
                  <div className="stat">
                    <div className="value" style={{
                      color: chartData[chartData.length - 1].close >= chartData[0].close ? 'var(--green)' : 'var(--red)',
                    }}>
                      {((chartData[chartData.length - 1].close - chartData[0].close) / chartData[0].close * 100).toFixed(2)}%
                    </div>
                    <div className="label">Period Change</div>
                  </div>
                )}
              </div>
              <div className="meta" style={{ marginTop: 8 }}>
                Period: {activeReport.startDate} — {activeReport.endDate}
              </div>
            </div>
          )}

          {/* Prediction Results */}
          {prediction && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                🔮 Earnings Impact Prediction
                <span style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: prediction.confidence === 'High' ? '#22c55e33' : prediction.confidence === 'Medium' ? '#f59e0b33' : '#ef444433',
                  color: prediction.confidence === 'High' ? '#22c55e' : prediction.confidence === 'Medium' ? '#f59e0b' : '#ef4444',
                }}>
                  {prediction.confidence} Confidence
                </span>
              </h4>

              {/* View toggle */}
              <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                {(['both', 'pattern', 'adjusted'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setShowPrediction(v)}
                    style={{
                      padding: '4px 12px',
                      fontSize: 12,
                      border: showPrediction === v ? '1px solid #8b5cf6' : '1px solid #2a2d3a',
                      borderRadius: 4,
                      background: showPrediction === v ? '#8b5cf622' : 'transparent',
                      color: showPrediction === v ? '#8b5cf6' : '#8b8fa3',
                      cursor: 'pointer',
                    }}
                  >
                    {v === 'both' ? 'Both' : v === 'pattern' ? 'Pattern Only' : 'News-Adjusted'}
                  </button>
                ))}
              </div>

              {/* Prediction chart: recent prices → predicted prices */}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={combinedPredictionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis dataKey="date" stroke="#8b8fa3" fontSize={12} />
                  <YAxis stroke="#8b8fa3" fontSize={12} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{
                      background: '#1a1d27',
                      border: '1px solid #2a2d3a',
                      borderRadius: 8,
                      color: '#e1e4eb',
                    }}
                    formatter={(value: any, name: any) =>
                      value != null ? [`$${Number(value).toFixed(2)}`, name] : ['-', name]
                    }
                  />
                  <ReferenceLine
                    x={combinedPredictionData.find((d) => d.date === 'Day +1')?.date ? (prediction.recentPrices || []).slice(-1)[0]?.date || '' : ''}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    label={{ value: 'Report Day →', position: 'top', fill: '#f59e0b', fontSize: 11 }}
                  />
                  <Line type="monotone" dataKey="close" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} name="Actual" connectNulls={false} />
                  {(showPrediction === 'both' || showPrediction === 'pattern') && (
                    <Line type="monotone" dataKey="patternClose" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: '#f59e0b' }} name="Pattern Prediction" connectNulls={false} />
                  )}
                  {(showPrediction === 'both' || showPrediction === 'adjusted') && (
                    <Line type="monotone" dataKey="adjustedClose" stroke="#22c55e" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3, fill: '#22c55e' }} name="News-Adjusted" connectNulls={false} />
                  )}
                  <Legend />
                </LineChart>
              </ResponsiveContainer>

              {/* Matched pattern info */}
              <div className="card" style={{ marginTop: 16 }}>
                <div className="stats-row">
                  <div className="stat">
                    <div className="value" style={{ color: '#f59e0b' }}>{prediction.matchedPattern.label}</div>
                    <div className="label">Matched Pattern</div>
                  </div>
                  <div className="stat">
                    <div className="value">{prediction.patternsAnalyzed}</div>
                    <div className="label">Patterns Analyzed</div>
                  </div>
                  <div className="stat">
                    <div className="value">{prediction.model}</div>
                    <div className="label">AI Model</div>
                  </div>
                </div>
                <div className="meta" style={{ marginTop: 8 }}>
                  <strong>Pattern similarity:</strong> {prediction.matchedPattern.similarity}
                </div>
                <div className="meta" style={{ marginTop: 4 }}>
                  <strong>Historical outcome:</strong> {prediction.matchedPattern.historicalOutcome}
                </div>
              </div>

              {/* Prediction table */}
              <div className="card" style={{ marginTop: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2a2d3a' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#8b8fa3' }}>Day</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#f59e0b' }}>Pattern $</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#f59e0b' }}>Pattern %</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e' }}>Adjusted $</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e' }}>Adjusted %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictionChartData.map((d) => (
                      <tr key={d.date} style={{ borderBottom: '1px solid #1a1d27' }}>
                        <td style={{ padding: '6px 8px' }}>{d.date}</td>
                        <td style={{ textAlign: 'right', padding: '6px 8px', color: '#f59e0b' }}>
                          ${d.patternClose.toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px', color: d.patternPct >= 0 ? '#22c55e' : '#ef4444' }}>
                          {d.patternPct >= 0 ? '+' : ''}{d.patternPct.toFixed(2)}%
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px', color: '#22c55e' }}>
                          ${d.adjustedClose.toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px', color: d.adjustedPct >= 0 ? '#22c55e' : '#ef4444' }}>
                          {d.adjustedPct >= 0 ? '+' : ''}{d.adjustedPct.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* AI reasoning */}
              <div className="card" style={{ marginTop: 12 }}>
                <div className="meta"><strong>Summary:</strong> {prediction.overallSummary}</div>
                <div className="meta" style={{ marginTop: 8 }}>
                  <strong>News adjustment reasoning:</strong> {prediction.newsAdjustmentReasoning}
                </div>
                <div className="meta" style={{ marginTop: 4, color: '#8b8fa3', fontSize: 11 }}>
                  Generated at {prediction.generatedAt} · Last close: ${typeof prediction.lastClose === 'number' ? prediction.lastClose.toFixed(2) : prediction.lastClose}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
